import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from "@angular/core";
import { NgbDate } from "@ng-bootstrap/ng-bootstrap";
import { Attachment, RateSchedule } from "../../../../inventory/inventory";
import { DeliveryOptions } from "../../../models/cart.model";
import { DateRange, Location, ModalService } from "../../../../core/core";
import { DAYS_PER_WEEK, DEFAULT_BILLING_CYCLE_DAYS } from "../../../rental-duration-billing";
import { SelfHaulInfoModalComponent } from "../../self-haul-info-modal/self-haul-info-modal.component";

/** Inclusive-day shortcuts shown after a start date is chosen. */
export const QuickRentalDuration = {
  Week: "week",
  TwoWeeks: "twoWeeks",
  Month: "month",
  TwoMonths: "twoMonths",
  ThreeMonths: "threeMonths",
} as const;

/** Inclusive day count for the 2 Weeks pill. */
export const TWO_WEEK_DURATION_DAYS = DAYS_PER_WEEK * 2;

/** Inclusive day counts for the 2 Month and 3 Month pills (28-day billing cycles). */
export const TWO_MONTH_DURATION_DAYS = DEFAULT_BILLING_CYCLE_DAYS * 2;
export const THREE_MONTH_DURATION_DAYS = DEFAULT_BILLING_CYCLE_DAYS * 3;

@Component({
  selector: "common-add-to-cart-dates",
  templateUrl: "./add-to-cart-dates.component.html",
  styleUrls: ["./add-to-cart-dates.component.scss"],
})
export class AddToCartDatesComponent implements OnInit {
  @Input() startDate: NgbDate;
  @Input() endDate: NgbDate;
  @Input() duration: number = 0;
  @Input() zipcode: string;
  @Input() showDeliveryAddress: boolean = false;
  @Input() deliveryAddress: Location;
  /** Centers the coordinates map when no jobsite address has been picked yet. */
  @Input() defaultMapLocation: Location;
  @Input() invalidDeliveryAddress: boolean;
  @Input() rpoEnabled: boolean;
  @Input() rpoInterested: boolean;
  @Input() rateSchedules: RateSchedule[];
  @Input() blockWeekends: boolean = true;
  @Input() showZipCode: boolean = true;
  @Input() deliveryOption: string = DeliveryOptions.Delivery;
  /** Machine id used by the embedded attachments picker (already-in-cart checks). */
  @Input() machineId: string;
  /** Fallback account/partition for cart attachment payload fields. */
  @Input() machineAccountId: string;
  @Input() attachments: Attachment[];
  @Input() attachmentsFromIndex: Attachment[];

  inCheckout: boolean;
  invalidZipCode: boolean;
  DeliveryOptions = DeliveryOptions;
  /** Show the (single) range calendar. Collapses to a summary once a full range is picked. */
  showCalendar: boolean = true;

  /** 1 Week / 2 Weeks / 1 Month / 2 Months / 3 Months pills — same visual language as attachment pills. */
  readonly quickDurations = [
    { id: QuickRentalDuration.Week, label: "1 Week", days: DAYS_PER_WEEK },
    { id: QuickRentalDuration.TwoWeeks, label: "2 Weeks", days: TWO_WEEK_DURATION_DAYS },
    { id: QuickRentalDuration.Month, label: "1 Month", days: DEFAULT_BILLING_CYCLE_DAYS },
    { id: QuickRentalDuration.TwoMonths, label: "2 Months", days: TWO_MONTH_DURATION_DAYS },
    { id: QuickRentalDuration.ThreeMonths, label: "3 Months", days: THREE_MONTH_DURATION_DAYS },
  ];

  @Output() startDateSelected = new EventEmitter<NgbDate>();
  @Output() endDateSelected = new EventEmitter<NgbDate>();
  @Output() durationSelected = new EventEmitter<number>();
  @Output() zipcodeChange = new EventEmitter<number>();
  @Output() locationSelected = new EventEmitter<Location>();
  @Output() rpoSelected = new EventEmitter<boolean>();
  @Output() deliveryOptionChange = new EventEmitter<string>();
  showBanner: boolean;

  constructor(private modalService: ModalService) {}

  ngOnInit(): void {
    // hack but it works
    this.inCheckout = window.location.pathname.includes("checkout");
    // If a full range is already selected, collapse the calendar to the summary.
    if (this.startDate && this.endDate) {
      this.showCalendar = false;
    }
  }

  /** True once both a start and end date have been chosen. */
  get rangeComplete(): boolean {
    return this.startDate != null && this.endDate != null;
  }

  /** Pills appear after a start date, and hide once a full range is chosen — same as the calendar. */
  get showQuickDurationPills(): boolean {
    return this.startDate != null && this.endDate == null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.zipcode && changes.zipcode.firstChange == false && changes.zipcode.currentValue === "00000") {
      this.invalidZipCode = true;
      this.zipcode = changes.zipcode.previousValue;
    }
  }

  /**
   * Handles selection from the single range calendar. The first click sets the
   * start date (range incomplete); a later second click sets the end date. When
   * the range is complete we emit the end date + derived duration and collapse
   * the calendar; while incomplete we reset the end/duration so the parent's
   * primary action button stays disabled.
   */
  onRangeSelected(range: DateRange) {
    this.startDate = range.fromDate;
    this.endDate = range.toDate && range.toDate.day != null ? range.toDate : null;

    this.startDateSelected.emit(this.startDate);
    this.updateWeekendBanner(this.startDate);

    if (this.startDate && this.endDate) {
      this.endDateSelected.emit(this.endDate);
      this.emitDerivedDuration();
      this.showCalendar = false; // full range chosen → show the summary
    } else {
      // Only a start date so far: keep the calendar open and reset downstream
      // state so the derived duration (and the primary action button) reflect that the
      // range is not yet complete.
      this.endDate = null;
      this.endDateSelected.emit(null);
      this.duration = 0;
      this.durationSelected.emit(0);
      this.showCalendar = true;
    }
  }

  /**
   * Fill the end date from a duration pill, then collapse to the
   * summary the same way a calendar-picked range does.
   */
  selectQuickDuration(inclusiveDays: number): void {
    if (!this.startDate) {
      return;
    }

    this.endDate = DateRange.addDays(this.startDate, inclusiveDays - 1);
    this.endDateSelected.emit(this.endDate);
    this.emitDerivedDuration();
    this.showCalendar = false;
  }

  /** True when the selected range matches a duration pill. */
  isQuickDurationSelected(inclusiveDays: number): boolean {
    if (!this.startDate || !this.endDate) {
      return false;
    }
    return new DateRange(this.startDate, this.endDate).calculateDaysDiff() === inclusiveDays;
  }

  getFormattedDate(): string {
    return this.formatNgbDate(this.startDate);
  }

  getFormattedEndDate(): string {
    return this.formatNgbDate(this.endDate);
  }

  private formatNgbDate(date: NgbDate): string {
    if (!date) return '';
    const jsDate = new Date(date.year, date.month - 1, date.day);
    return jsDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }

  /** Reopen the range calendar to change the selected dates. */
  editDate(): void {
    this.showCalendar = true;
  }

  /**
   * Show the "orders placed this weekend may not be available for weekend
   * delivery" banner when the chosen start date falls on the current weekend.
   */
  private updateWeekendBanner(startDate: NgbDate) {
    if (!startDate) {
      this.showBanner = false;
      return;
    }

    const normalize = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const selectedNormalized = normalize(new Date(startDate.year, startDate.month - 1, startDate.day));
    const todayNormalized = normalize(new Date());

    // Only continue if today is Friday–Sunday
    const todayDay = todayNormalized.getDay(); // 5 = Fri, 6 = Sat, 0 = Sun
    if (![5, 6, 0].includes(todayDay)) {
      this.showBanner = false;
      return;
    }

    // Get this weekend's Friday and Sunday dates
    const friday = new Date(todayNormalized);
    if (todayDay === 6) friday.setDate(todayNormalized.getDate() - 1);
    // Saturday → back to Friday
    else if (todayDay === 0) friday.setDate(todayNormalized.getDate() - 2); // Sunday → back to Friday

    const sunday = new Date(friday);
    sunday.setDate(friday.getDate() + 2);

    // Check if selected date is in this weekend
    this.showBanner = selectedNormalized >= friday && selectedNormalized <= sunday;
  }

  /**
   * Derive the rental duration (inclusive day count of the selected start/end
   * dates) and emit it. Sending the exact day count preserves the renter's
   * chosen end date end-to-end (the backend stores
   * `EndDate = BeginDate + Duration - 1`). Whether the rental is billed weekly
   * or monthly is decided downstream by the shared "< 21 days => weekly" rule.
   */
  private emitDerivedDuration(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.duration = new DateRange(this.startDate, this.endDate).calculateDaysDiff();
    this.durationSelected.emit(this.duration);
  }

  onZipcodeChange(zipcode: number) {
    this.zipcodeChange.emit(zipcode);
    this.invalidZipCode = false;
  }

  onLocationSelected(location: Location) {
    this.deliveryAddress = location;
    this.locationSelected.emit(location);
  }

  rpoChange(event) {
    this.rpoInterested = event;
    this.rpoSelected.emit(event);
  }

  onDeliveryOptionChange(option: string) {
    this.deliveryOption = option;
    this.deliveryOptionChange.emit(option);
    
    // Show self-haul requirements modal when user selects PickUp
    if (option === DeliveryOptions.PickUp) {
      this.modalService.standardModal(SelfHaulInfoModalComponent, false);
    }
  }

}
